import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Check, UploadCloud, ChevronRight, ChevronLeft, FileText, X, Copy, LayoutDashboard } from "lucide-react";

const ARTICLE_TYPES = ["Research Article", "Review", "Case Study", "Short Communication", "Editorial"];
const STEPS = ["Manuscript", "Authors", "Content", "Review"];

export default function Submit() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    title: "", abstract: "", keywords: "", article_type: "Research Article",
    journal_slug: "", special_issue_id: "none", manuscript_text: "", file_url: "", file_name: "", cover_letter: "",
    authors: [{ name: "", affiliation: "", email: "", is_corresponding: true }],
  });

  useEffect(() => {
    api.get("/journals").then(({ data }) => setJournals(data)).catch(() => {});
    api.get("/special-issues").then(({ data }) => setSpecials(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const js = new URLSearchParams(window.location.search).get("journal");
    if (js) setForm((f) => ({ ...f, journal_slug: js }));
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user && form.authors[0].name === "") {
      setForm((f) => ({ ...f, authors: [{ name: user.name, affiliation: user.affiliation || "", email: user.email, is_corresponding: true }] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setAuthor = (i, k, v) => {
    const authors = [...form.authors];
    authors[i] = { ...authors[i], [k]: v };
    setForm({ ...form, authors });
  };
  const addAuthor = () => setForm({ ...form, authors: [...form.authors, { name: "", affiliation: "", email: "", is_corresponding: false }] });
  const removeAuthor = (i) => setForm({ ...form, authors: form.authors.filter((_, idx) => idx !== i) });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Please upload a PDF or DOCX file."); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds the 25MB limit."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, file_url: abs, file_name: data.filename }));
      toast.success("Manuscript file uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const canNext = () => {
    if (step === 0) return form.title.trim() && form.journal_slug && form.article_type;
    if (step === 1) return form.authors.every((a) => a.name.trim());
    if (step === 2) return form.abstract.trim();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/papers", {
        ...form,
        special_issue_id: form.special_issue_id === "none" ? null : form.special_issue_id,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Manuscript submitted!");
      setDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    if (!done?.submission_id) return;
    navigator.clipboard?.writeText(done.submission_id);
    toast.success("Paper ID copied to clipboard");
  };

  if (loading) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="submit-success">
      <div className="grid place-items-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-5"><Check className="w-8 h-8" /></div>
      <h1 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">Manuscript submitted</h1>
      <p className="text-slate-500 mt-2">Thank you — a confirmation email with your Paper ID is on its way. Please quote this ID in all correspondence.</p>
      <div className="mt-8 bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Your Paper ID</p>
        <div className="flex items-center justify-center gap-3">
          <span className="font-mono text-2xl font-bold text-amber-600" data-testid="success-paper-id">{done.submission_id}</span>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={copyId} data-testid="copy-paper-id-btn"><Copy className="w-4 h-4" /> Copy</Button>
        </div>
        <p className="text-sm text-slate-500 mt-3 italic">{done.title}</p>
      </div>
      <div className="flex items-center justify-center gap-3 mt-8">
        <Button className="rounded-full gap-1.5" onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn"><LayoutDashboard className="w-4 h-4" /> Go to my submissions</Button>
        <Button variant="outline" className="rounded-full" onClick={() => window.location.reload()} data-testid="submit-another-btn">Submit another</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center gap-2 mb-2 text-amber-600">
        <UploadCloud className="w-5 h-5" />
        <span className="font-mono text-xs uppercase tracking-widest font-semibold">Submit Manuscript</span>
      </div>
      <h1 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">New Submission</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mt-8 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`grid place-items-center w-8 h-8 rounded-full text-sm font-semibold shrink-0 transition-colors ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-primary text-white" : "bg-slate-200 dark:bg-slate-700 text-slate-500"}`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm hidden sm:block ${i === step ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-500"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />}
          </div>
        ))}
      </div>

      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 md:p-8">
        {step === 0 && (
          <div className="space-y-5" data-testid="submit-step-manuscript">
            <div>
              <Label>Article title *</Label>
              <Input data-testid="submit-title" value={form.title} onChange={set("title")} className="mt-1.5" placeholder="Enter the full title of your manuscript" />
            </div>
            <div>
              <Label>Target journal *</Label>
              <Select value={form.journal_slug} onValueChange={(v) => setForm({ ...form, journal_slug: v })}>
                <SelectTrigger data-testid="submit-journal" className="mt-1.5"><SelectValue placeholder="Select a journal" /></SelectTrigger>
                <SelectContent>
                  {journals.map((j) => <SelectItem key={j.slug} value={j.slug}>{j.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Article type *</Label>
              <Select value={form.article_type} onValueChange={(v) => setForm({ ...form, article_type: v })}>
                <SelectTrigger data-testid="submit-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ARTICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Special issue</Label>
              <Select value={form.special_issue_id} onValueChange={(v) => setForm({ ...form, special_issue_id: v })}>
                <SelectTrigger data-testid="submit-special-issue" className="mt-1.5"><SelectValue placeholder="Not applicable" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not applicable</SelectItem>
                  {specials.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">Choose an open special issue to submit to, or leave as "Not applicable".</p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5" data-testid="submit-step-authors">
            {form.authors.map((a, i) => (
              <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Author {i + 1}</span>
                  {form.authors.length > 1 && (
                    <button onClick={() => removeAuthor(i)} data-testid={`remove-author-${i}`} className="text-destructive hover:bg-destructive/10 p-1 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid={`author-name-${i}`} placeholder="Full name *" value={a.name} onChange={(e) => setAuthor(i, "name", e.target.value)} />
                  <Input placeholder="Email" value={a.email} onChange={(e) => setAuthor(i, "email", e.target.value)} />
                  <Input className="sm:col-span-2" placeholder="Affiliation" value={a.affiliation} onChange={(e) => setAuthor(i, "affiliation", e.target.value)} />
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm text-slate-600 dark:text-slate-400">
                  <input type="checkbox" checked={a.is_corresponding} onChange={(e) => setAuthor(i, "is_corresponding", e.target.checked)} />
                  Corresponding author
                </label>
              </div>
            ))}
            <Button variant="outline" onClick={addAuthor} data-testid="add-author-btn" className="rounded-full gap-1.5">
              <Plus className="w-4 h-4" /> Add author
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="submit-step-content">
            <div>
              <Label>Abstract *</Label>
              <Textarea data-testid="submit-abstract" value={form.abstract} onChange={set("abstract")} rows={6} className="mt-1.5" placeholder="Summarise your research…" />
            </div>
            <div>
              <Label>Keywords (comma separated)</Label>
              <Input data-testid="submit-keywords" value={form.keywords} onChange={set("keywords")} className="mt-1.5" placeholder="machine learning, genomics, climate" />
            </div>
            <div>
              <Label>Full manuscript text (optional)</Label>
              <Textarea data-testid="submit-manuscript" value={form.manuscript_text} onChange={set("manuscript_text")} rows={6} className="mt-1.5" placeholder="Paste the full text, or leave blank and upload a file link below." />
            </div>
            <div>
              <Label>Manuscript file (PDF or DOCX)</Label>
              {form.file_url ? (
                <div className="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3" data-testid="uploaded-file">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium truncate">{form.file_name}</span>
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, file_url: "", file_name: "" })} data-testid="remove-file-btn" className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900">
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              ) : (
                <label className="mt-1.5 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 p-6 cursor-pointer hover:border-primary/50 transition-colors" data-testid="file-dropzone">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : <UploadCloud className="w-6 h-6 text-slate-400" />}
                  <span className="text-sm text-slate-500">{uploading ? "Uploading…" : "Click to upload your manuscript (max 25MB)"}</span>
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFile} data-testid="submit-file-input" disabled={uploading} />
                </label>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="submit-step-review">
            <h3 className="font-serif font-bold text-lg">Review &amp; confirm</h3>
            <dl className="text-sm space-y-2">
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Title</dt><dd className="font-medium">{form.title}</dd></div>
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Journal</dt><dd className="font-medium">{journals.find((j) => j.slug === form.journal_slug)?.title}</dd></div>
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Type</dt><dd className="font-medium">{form.article_type}</dd></div>
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Special issue</dt><dd className="font-medium">{form.special_issue_id === "none" ? "Not applicable" : specials.find((s) => s.id === form.special_issue_id)?.title}</dd></div>
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Authors</dt><dd className="font-medium">{form.authors.map((a) => a.name).join(", ")}</dd></div>
              <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Abstract</dt><dd className="text-slate-600 dark:text-slate-400 line-clamp-3">{form.abstract}</dd></div>
            </dl>
            <div>
              <Label>Cover letter (optional)</Label>
              <Textarea data-testid="submit-cover-letter" value={form.cover_letter} onChange={set("cover_letter")} rows={3} className="mt-1.5" placeholder="A note to the editor…" />
            </div>
            <p className="text-xs text-slate-500">By submitting you confirm this is original work not under consideration elsewhere.</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
          <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="submit-back-btn" className="gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="submit-next-btn" className="rounded-full gap-1">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} data-testid="submit-final-btn" className="rounded-full gap-1.5 bg-amber-500 hover:bg-amber-600 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Submit Manuscript</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
